import { SagaIterator } from 'redux-saga';
import { select } from 'redux-saga/effects';
import { App } from '@ps20-frontend/frontend-models';
import {
  AngebotsId,
  GlobalAction,
  WrappedSavedStates,
  WrappedSavedStatesLoaded
} from '@ps20-frontend/shop-integration-redux';
import { IAppState } from '../appState';

interface VwgV2StateForNbgCleanup {
  angebotsId?: string;
  gebaeude?: {
    nebengebaeude?: {
      totalArea?: number | null;
    };
  };
}

interface WarenkorbAngebotForNbgCleanup {
  appId: App;
  angebotsId: string;
  beibehalten: boolean;
  nebengebaeudeUeber200?: boolean;
}

function isObsoleteNbgProduct(
  angebot: WarenkorbAngebotForNbgCleanup,
  vwgStateByAngebotsId: Map<string, VwgV2StateForNbgCleanup>,
): boolean {
  const vwgState = vwgStateByAngebotsId.get(angebot.angebotsId);
  if (!vwgState) {
    return false;
  }
  const totalArea = vwgState.gebaeude?.nebengebaeude?.totalArea;
  if (typeof totalArea !== 'number' || totalArea <= 200) {
    return false;
  }
  // undefined/null: the offer was saved before the field was introduced (frontend release 2026-07-29) and must be deleted.
  // false is a valid value (e.g. outbuilding coverage deselected) and must not be deleted.
  return angebot.nebengebaeudeUeber200 !== true && angebot.nebengebaeudeUeber200 !== false;
}

export function* collectOldNbgProducts(
  action: GlobalAction<WrappedSavedStatesLoaded>,
): SagaIterator<AngebotsId[]> {
  const isNbgToggleEnabled: boolean = (yield select(
    (s: IAppState) => s.transientState?.featureToggles?.['UW_NEBENGEBAEUDE'] === true,
  )) as boolean;

  if (!isNbgToggleEnabled) {
    return [];
  }

  const actionPayload: WrappedSavedStates = action.payload.savedStatesLoaded;
  const loadedAppState = actionPayload.find(
    loadedState => loadedState.appId === App.SHOP_DASHBOARD
  );

  if (!loadedAppState) {
    return [];
  }

  const warenkorb = loadedAppState.state.warenkorb as WarenkorbAngebotForNbgCleanup[];

  if (!warenkorb || warenkorb.length === 0) {
    return [];
  }

  const vwgV2Candidates = warenkorb.filter(
    angebot => angebot.appId === App.VWG_V2 && angebot.beibehalten !== true,
  );

  if (vwgV2Candidates.length === 0) {
    return [];
  }

  const vwgV2StatesMap = new Map<string, VwgV2StateForNbgCleanup>();
  actionPayload
    .filter(loadedState => loadedState.appId === App.VWG_V2)
    .forEach(vwgV2State => {
      const typedState = vwgV2State.state as VwgV2StateForNbgCleanup;
      const angebotsId = typedState.angebotsId;
      if (angebotsId) {
        vwgV2StatesMap.set(angebotsId, typedState);
      }
    });

  return vwgV2Candidates.filter(
    angebot => isObsoleteNbgProduct(angebot, vwgV2StatesMap),
  );
}
